import { useEffect, useState } from "react";

import { TickIcon } from "@/assets/icons";
import { CopyIcon } from "@/components/ui/icons/CopyIcon";
import { cn } from "@/lib/cn";

interface CopyButtonProps {
  /** What is copied, e.g. "login": names the button and the announcement */
  label: string;
  value: string;
  className?: string;
}

/**
 * An icon button that sits right after a value in a label/value row, where {@link CopyableValue}
 * with its own label and full-width code block would repeat the row's label.
 */
export const CopyButton = ({ label, value, className }: CopyButtonProps) => {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (copyState !== "copied") return;
    const timeout = setTimeout(() => setCopyState("idle"), 3000);
    return () => clearTimeout(timeout);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      // navigator.clipboard is undefined on insecure (http) origins, e.g. local dev
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        title={copyState === "copied" ? "Copied" : `Copy ${label}`}
        onClick={handleCopy}
        className={cn(
          "inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent align-middle text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:size-3.5 pointer-coarse:size-11",
          copyState === "copied" && "text-success hover:text-success",
          className,
        )}
      >
        {copyState === "copied" ? <TickIcon aria-hidden /> : <CopyIcon color="currentColor" />}
      </button>
      {/* The button keeps its name, so the outcome is announced from a region that is always there */}
      <span role="status" className="sr-only">
        {copyState === "copied" && `${label} copied`}
      </span>
      {copyState === "failed" && (
        <span role="alert" className="mt-1 block text-xs text-destructive-fg">
          Couldn't copy automatically. Select the {label} and press Ctrl+C.
        </span>
      )}
    </>
  );
};
